import type { CustomCellRendererProps } from "ag-grid-react";
import type { JSX } from "react";

import { CellType } from "../../api/openapi.ts";
import { BlastAlignmentCell } from "../cells/BlastAlignmentCell.tsx";
import { BooleanListCell } from "../cells/BooleanListCell.tsx";
import { ColoredLabelCell } from "../cells/ColoredLabelCell.tsx";
import { ColoredLabelListCell } from "../cells/ColoredLabelListCell.tsx";
import { DateCell } from "../cells/DateCell.tsx";
import { DateListCell } from "../cells/DateListCell.tsx";
import { DefaultCell } from "../cells/DefaultCell.tsx";
import { LinkCell } from "../cells/LinkCell.tsx";
import { LinkExternalCell } from "../cells/LinkExternalCell.tsx";
import { LinkExternalListCell } from "../cells/LinkExternalListCell.tsx";
import { LinkListCell } from "../cells/LinkListCell.tsx";
import { NumberCell } from "../cells/NumberCell.tsx";
import { NumberListCell } from "../cells/NumberListCell.tsx";
import { PercentCell } from "../cells/PercentCell.tsx";
import { SampleCell } from "../cells/SampleCell.tsx";
import { StrandCell } from "../cells/StrandCell.tsx";
import { StrandListCell } from "../cells/StrandListCell.tsx";
import { TextCell } from "../cells/TextCell.tsx";
import { TextListCell } from "../cells/TextListCell.tsx";

const RENDERER_REGISTRY: Record<CellType, (params: CustomCellRendererProps) => JSX.Element> = {
  // Text
  [CellType.STRING]: TextCell,

  //Text List
  [CellType.STRING_LIST]: TextListCell,
  [CellType.NESTED_STRING_LIST]: TextListCell, // text (-> default) cell flats nested lists for now

  // Date
  [CellType.DATE]: DateCell,
  [CellType.DATE_LIST]: DateListCell,
  [CellType.DATE_TIME]: DateCell, // date cell remove 00:00 time automatically

  //Number
  [CellType.LONG]: NumberCell,
  [CellType.INTEGER]: NumberCell,
  [CellType.SCIENTIFIC]: NumberCell,
  [CellType.FLOAT]: NumberCell,
  [CellType.DOUBLE]: NumberCell,
  [CellType.BIG_DECIMAL]: NumberCell,
  [CellType.PERCENT]: PercentCell,

  //Number list
  [CellType.LONG_LIST]: NumberListCell,
  [CellType.INTEGER_LIST]: NumberListCell,
  [CellType.FLOAT_LIST]: NumberListCell,
  [CellType.DOUBLE_LIST]: NumberListCell,
  [CellType.NESTED_INTEGER_LIST]: NumberListCell, // number list cell flats nested lists for now
  [CellType.NESTED_LONG_LIST]: NumberListCell, // number list cell flats nested lists for now
  [CellType.NESTED_FLOAT_LIST]: NumberListCell, // number list cell flats nested lists for now
  [CellType.NESTED_DOUBLE_LIST]: NumberListCell, // number list cell flats nested lists for now

  // Boolean
  [CellType.BOOLEAN]: BooleanListCell,
  [CellType.BOOLEAN_LIST]: BooleanListCell,

  // Links
  [CellType.LINK]: LinkCell,
  [CellType.LINK_EXTERNAL]: LinkExternalCell,
  [CellType.LINK_LIST]: LinkListCell,
  [CellType.LINK_EXTERNAL_LIST]: LinkExternalListCell,

  // Sample
  [CellType.SAMPLE]: SampleCell,

  // Strand
  [CellType.STRAND]: StrandCell,
  [CellType.STRAND_LIST]: StrandListCell,

  // Entitiy Types
  [CellType.COLOR_STRING]: ColoredLabelCell,
  [CellType.COLOR_STRING_LIST]: ColoredLabelListCell,
  [CellType.COLOR_NUM]: ColoredLabelCell,
  [CellType.COLOR_NUM_LIST]: ColoredLabelListCell,

  // Alignment
  [CellType.BLAST_ALIGNMENT_LIST]: BlastAlignmentCell,

  // TODO Missing renderers for cells
  [CellType.FILE]: DefaultCell,
  [CellType.FILE_LIST]: DefaultCell,
  [CellType.ORGANISM]: DefaultCell,

  [CellType.VARIANT_PROFILE]: DefaultCell,
  // For synteny viewer tables we have no use case for a sample list since every organism
  // cannot have more than one sample associated. The multi-value sample case exists only
  // in the resource column of playbook events
  [CellType.SAMPLE_LIST]: DefaultCell,
};

export const RendererCellForType = ({ type }: { type: CellType }) => {
  const CellComponent = RENDERER_REGISTRY[type] ?? DefaultCell;

  const WrappedRenderer = (props: CustomCellRendererProps) => <CellComponent {...props} />;
  WrappedRenderer.displayName = `RendererCell(${type})`;

  return WrappedRenderer;
};
